import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EMPTY, Observable, finalize } from 'rxjs';
import { MainHeaderComponent } from '../../shared/main-header/main-header.component';
import {
  CollectorDatasetsService,
  CreateCollectorDatasetResponse,
  DatasetFile,
  DatasetOperationScope,
  ParsedValidationError,
  parseCollectorDatasetValidationError,
  ValidationStatus
} from '../../../services/collector-datasets.service';
import { AuthService } from '../../../services/auth.service';
import { ListCollectorDatasetsComponent } from '../list-collector-datasets/list-collector-datasets.component';
import { DatasetSchema, SchemasService } from '../../../services/schemas.service';
import { SchemaStructureDialogComponent } from './schema-structure-dialog.component';
import { CollectorDatasetDetailDialogComponent } from '../list-collector-datasets/collector-dataset-detail-dialog.component';
import { DialogConfirmMessageComponent } from '../../shared/dialog-confirm-message/dialog-confirm-message.component';

@Component({
  selector: 'app-collector-datasets',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MainHeaderComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
    DropdownModule,
    ListCollectorDatasetsComponent
  ],
  templateUrl: './collector-datasets.component.html',
  styleUrl: './collector-datasets.component.scss'
})
export class CollectorDatasetsComponent implements OnInit, OnDestroy {
  @ViewChild(ListCollectorDatasetsComponent) private historyList?: ListCollectorDatasetsComponent;
  private readonly resizeStorageKey = 'collector-datasets-upload-pane-width';
  private readonly minUploadPanePercent = 35;
  private readonly maxUploadPanePercent = 80;
  private readonly resizeDesktopBreakpoint = 1040;
  private removeResizeListeners: (() => void) | null = null;

  isSaving = false;
  isTrackingSubmission = false;
  isDragActive = false;
  isResizingLayout = false;
  selectedFile: File | null = null;
  fileName = '';
  currentUserData: any = null;
  liveDataset$: Observable<DatasetFile | null> = EMPTY;
  uploadPaneWidthPercent = 40;
  isXlsxOptionsExpanded = false;
  xlsxSheetName = '';
  xlsxHeaderRow: number | null = null;
  operationType: 'insert' | 'full_replace' | 'replace_scope' = 'insert';
  fullReplaceConfirm = false;
  scopeColumn = '';
  scopeValueSingle = '';
  private lastSubmission: {
    file: File;
    operationType: 'insert' | 'full_replace' | 'replace_scope';
    fullReplaceConfirm: boolean;
    scopeColumn: string;
    scopeValueSingle: string;
    isXlsxOptionsExpanded: boolean;
    xlsxSheetName: string;
    xlsxHeaderRow: number | null;
  } | null = null;
  showAllMissingFields = false;
  readonly missingFieldsPreviewCount = 12;
  private parsedErrorCache: { raw: string; value: ParsedValidationError | null } | null = null;

  readonly operationTypeOptions = [
    { label: 'Agregar (suma los datos del archivo a los que ya existen)', value: 'insert' },
    { label: 'Reemplazar todo (borra todos los datos actuales y carga solo el archivo)', value: 'full_replace' },
    { label: 'Reemplazar por columna (borra solo los datos que coincidan y carga el archivo)', value: 'replace_scope' }
  ];

  readonly operationCards: Array<{
    value: 'insert' | 'full_replace' | 'replace_scope';
    title: string;
    description: string;
    icon: string;
  }> = [
    {
      value: 'insert',
      title: 'Agregar',
      description: 'Suma las filas del archivo a los datos actuales sin borrar información existente.',
      icon: 'add_circle'
    },
    {
      value: 'full_replace',
      title: 'Reemplazar',
      description: 'Borra todo el contenido actual del dataset y deja únicamente lo que viene en este archivo.',
      icon: 'warning'
    },
    {
      value: 'replace_scope',
      title: 'Actualizar por columna',
      description: 'Borra solo los registros que coincidan con una columna y valor, y luego carga el archivo.',
      icon: 'filter_alt'
    }
  ];

  get scopeColumnOptions() {
    return this.schemaColumns.map((column) => ({
      label: column.label || column.name,
      value: column.name
    }));
  }
  schemaId = '';
  schemaName = '';
  schemaDetails: DatasetSchema | null = null;
  isSchemaLoading = false;
  schemaLoadError = '';
  availableSchemas: DatasetSchema[] = [];
  isSchemasListLoading = false;
  schemasListError = '';
  schemaPickerValue = '';
  columnPage = 0;
  readonly columnPageSize = 8;
  readonly schemaSkeletonSlots = Array.from({ length: 8 });
  columnSearch = '';
  columnFilter: 'all' | 'required' = 'all';

  constructor(
    private route: ActivatedRoute,
    private collectorDatasetsService: CollectorDatasetsService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private schemasService: SchemasService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.restoreUploadPaneWidth();
    this.authService.userData$.subscribe((user) => {
      this.currentUserData = user;
    });
    this.schemaId = this.route.snapshot.queryParamMap.get('schema_id') ?? '';
    this.schemaName = this.route.snapshot.queryParamMap.get('schema_name') ?? '';
    if (this.schemaId) {
      this.loadSchemaDetails();
    } else {
      this.loadAvailableSchemas();
    }
  }

  ngOnDestroy(): void {
    this.detachResizeListeners();
  }

  get canSave(): boolean {
    if (!this.selectedFile || !this.schemaId || this.isSaving) return false;
    if (this.operationType === 'full_replace') return this.fullReplaceConfirm;
    if (this.operationType === 'replace_scope') return this.isScopeValid;
    return true;
  }

  get uploadHeaderStatusLabel(): string {
    if (this.isSaving) return 'Enviando archivo';
    if (this.isTrackingSubmission) return 'Carga en seguimiento';
    if (!this.selectedFile) return 'Esperando archivo';
    if (this.canSave) return 'Listo para enviar';
    if (this.operationType === 'full_replace' && !this.fullReplaceConfirm) return 'Falta confirmar';
    if (this.operationType === 'replace_scope') return 'Completa la configuración';
    return 'Revisa la carga';
  }

  get isScopeValid(): boolean {
    return !!this.scopeColumn && this.scopeValueSingle.trim().length > 0;
  }

  get pageTitle(): string {
    return 'Crear dataset';
  }

  get canResizeLayout(): boolean {
    return typeof window !== 'undefined' && window.innerWidth > this.resizeDesktopBreakpoint;
  }

  get schemaColumns() {
    return this.schemaDetails?.columns ?? this.schemaDetails?.schema ?? [];
  }

  get requiredColumnsCount(): number {
    return this.schemaColumns.filter(c => c.required).length;
  }

  get displayedColumns() {
    const term = this.columnSearch.trim().toLowerCase();
    let cols = this.schemaColumns;

    if (this.columnFilter === 'required') {
      cols = cols.filter((c) => c.required);
    }
    if (term) {
      cols = cols.filter((c) =>
        String(c.label || '').toLowerCase().includes(term) ||
        String(c.name || '').toLowerCase().includes(term)
      );
    }

    // Requeridas primero (el backend valida por nombre de columna, no por posición).
    return [...cols].sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0));
  }

  get visibleColumns() {
    const start = this.columnPage * this.columnPageSize;
    return this.schemaColumns.slice(start, start + this.columnPageSize);
  }

  get totalColumnPages(): number {
    return Math.ceil(this.schemaColumns.length / this.columnPageSize);
  }

  get hasNextColumnPage(): boolean {
    return this.columnPage < this.totalColumnPages - 1;
  }

  get hasPrevColumnPage(): boolean {
    return this.columnPage > 0;
  }

  nextColumnPage(): void {
    if (this.hasNextColumnPage) this.columnPage++;
  }

  prevColumnPage(): void {
    if (this.hasPrevColumnPage) this.columnPage--;
  }

  getColumnTooltip(column: any): string {
    const lines: string[] = [
      `Campo: ${column.name || '-'}`,
      `Tipo: ${column.data_type || 'text'}`,
      `Requerida: ${column.required ? 'Sí' : 'No'}`,
    ];
    if (column.unique)                     lines.push('Único: Sí');
    if (column.description)                lines.push(`Desc: ${column.description}`);
    if (column.example)                    lines.push(`Ej: ${column.example}`);
    if (column.default_value)              lines.push(`Default: ${column.default_value}`);
    if (column.date_format)                lines.push(`Formato: ${column.date_format}`);
    if (column.max_length   != null)       lines.push(`Máx. chars: ${column.max_length}`);
    if (column.min_value    != null)       lines.push(`Mín: ${column.min_value}`);
    if (column.max_value    != null)       lines.push(`Máx: ${column.max_value}`);
    if (column.precision    != null)       lines.push(`Precisión: ${column.precision}`);
    if (column.scale        != null)       lines.push(`Decimales: ${column.scale}`);
    if (column.regex)                      lines.push(`Regex: ${column.regex}`);
    if (column.enum_values?.length)        lines.push(`Valores: ${column.enum_values.join(', ')}`);
    return lines.join('\n');
  }

  getColumnTypeIcon(type?: string): string {
    switch ((type || 'text').toLowerCase()) {
      case 'integer':
      case 'decimal':
      case 'number':
      case 'float':
      case 'double':
        return 'tag';
      case 'boolean':
        return 'toggle_on';
      case 'date':
      case 'timestamp':
      case 'datetime':
        return 'event';
      case 'json':
      case 'object':
        return 'data_object';
      default:
        return 'short_text';
    }
  }

  get currentStep(): 1 | 2 | 3 {
    if (!this.selectedFile) return 1;
    if (this.isSaving) return 3;
    return 2;
  }

  get currentStepLabel(): string {
    if (this.isSaving) return 'Enviando archivo...';
    if (this.selectedFile) return 'Archivo listo';
    return 'Esperando archivo';
  }

  get selectedFileType(): string {
    return this.getFileType(this.selectedFile?.name ?? '') ?? '-';
  }

  get selectedFileSizeLabel(): string {
    return this.selectedFile ? this.formatFileSize(this.selectedFile.size) : '-';
  }

  get schemaStatusLabel(): string {
    if (this.isSchemaLoading) return 'Cargando estructura';
    if (this.schemaLoadError) return 'Estructura no disponible';
    if (this.schemaColumns.length) return `${this.schemaColumns.length} columnas`;
    return 'Sin columnas';
  }

  get timelineSteps(): Array<{ key: string; label: string; description: string }> {
    return [
      { key: 'selected', label: 'Archivo seleccionado', description: 'El archivo ya está listo para enviarse.' },
      { key: 'uploaded', label: 'Backend recibido', description: 'La API crea el job y guarda el archivo.' },
      { key: 'queued', label: 'En cola', description: 'Se publica el mensaje en Pub/Sub.' },
      { key: 'processing', label: 'Procesando', description: 'El worker valida y aplica el archivo.' },
      { key: 'completed', label: 'Resultado final', description: 'El proceso termina en completado o fallido.' }
    ];
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.clearValidation();
    this.fileName = file?.name ?? '';
    this.isDragActive = false;

    if (!file) return;

    const fileType = this.getFileType(file.name);
    if (!fileType) {
      this.selectedFile = null;
      this.snackBar.open(
        'Tipo de archivo no permitido. Solo se admite .csv o .xlsx.',
        'Cerrar',
        { duration: 4000 }
      );
      return;
    }

    this.selectedFile = file;
  }

  onDropzoneClick(event: MouseEvent, fileInput: HTMLInputElement): void {
    // Toda la zona abre el selector, salvo que el clic venga de un control interno
    // (botón "Seleccionar archivo" o "Descargar plantilla"), que ya tienen su propia acción.
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, .template-link-wrap')) return;
    fileInput.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragActive = false;

    const file = event.dataTransfer?.files?.[0] ?? null;
    if (!file) return;

    const fakeEvent = {
      target: { files: [file] }
    } as unknown as Event;
    this.onFileSelected(fakeEvent);
  }

  cancelSelectedFile(fileInput: HTMLInputElement): void {
    if (this.isSaving) return;
    this.selectedFile = null;
    this.fileName = '';
    fileInput.value = '';
    this.clearValidation();
  }

  startNewUpload(fileInput: HTMLInputElement): void {
    fileInput.value = '';
    this.selectedFile = null;
    this.fileName = '';
    this.clearValidation();
  }

  canRetryFailedUpload(): boolean {
    return !!this.lastSubmission;
  }

  retryFailedUpload(): void {
    const last = this.lastSubmission;
    if (!last) return;

    // Volvemos al formulario con el archivo y la configuración de la carga fallida,
    // para que el usuario ajuste (o reemplace el archivo) y reenvíe sin rehacer todo.
    this.isTrackingSubmission = false;
    this.liveDataset$ = EMPTY;
    this.selectedFile = last.file;
    this.fileName = last.file.name;
    this.operationType = last.operationType;
    this.fullReplaceConfirm = last.fullReplaceConfirm;
    this.scopeColumn = last.scopeColumn;
    this.scopeValueSingle = last.scopeValueSingle;
    this.isXlsxOptionsExpanded = last.isXlsxOptionsExpanded;
    this.xlsxSheetName = last.xlsxSheetName;
    this.xlsxHeaderRow = last.xlsxHeaderRow;
  }

  toggleXlsxOptions(panelBody: HTMLElement): void {
    this.isXlsxOptionsExpanded = !this.isXlsxOptionsExpanded;
    if (!this.isXlsxOptionsExpanded || typeof requestAnimationFrame === 'undefined') return;

    // Espera a que Angular pinte el contenido del acordeón antes de llevarlo al área visible.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panelBody.scrollTo({
          top: panelBody.scrollHeight,
          behavior: 'smooth'
        });
      });
    });
  }

  startHorizontalResize(event: PointerEvent): void {
    if (!this.canResizeLayout) return;

    event.preventDefault();
    this.isResizingLayout = true;

    const onPointerMove = (moveEvent: PointerEvent) => {
      this.updateUploadPaneWidthFromClientX(moveEvent.clientX);
    };

    const onPointerUp = () => {
      this.isResizingLayout = false;
      this.persistUploadPaneWidth();
      this.detachResizeListeners();
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
    this.removeResizeListeners = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }

  saveDataset(): void {
    if (!this.canSave || !this.selectedFile) return;

    if (!this.currentUserData?.uid) {
      this.snackBar.open('No fue posible identificar el usuario actual.', 'Cerrar', {
        duration: 4000
      });
      return;
    }

    if (this.operationType === 'replace_scope' && !this.isScopeValid) return;
    if (this.operationType === 'full_replace' && !this.fullReplaceConfirm) return;

    // Reemplazo total es destructivo: pedimos una confirmación final explícita.
    if (this.operationType === 'full_replace') {
      const datasetLabel = this.schemaName || this.schemaId;
      this.dialog
        .open(DialogConfirmMessageComponent, {
          width: '440px',
          maxWidth: '94vw',
          autoFocus: false,
          data: {
            message: `Esta acción borrará TODOS los datos actuales de "${datasetLabel}" y los reemplazará por el contenido de "${this.selectedFile.name}". Esta operación no se puede deshacer. ¿Deseas continuar?`
          }
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) this.performUpload();
        });
      return;
    }

    this.performUpload();
  }

  private performUpload(): void {
    if (!this.selectedFile) return;
    const selectedFile = this.selectedFile;

    const scope = this.buildScope();

    // Guardamos la configuración enviada para poder "corregir y reenviar" si falla.
    this.lastSubmission = {
      file: selectedFile,
      operationType: this.operationType,
      fullReplaceConfirm: this.fullReplaceConfirm,
      scopeColumn: this.scopeColumn,
      scopeValueSingle: this.scopeValueSingle,
      isXlsxOptionsExpanded: this.isXlsxOptionsExpanded,
      xlsxSheetName: this.xlsxSheetName,
      xlsxHeaderRow: this.xlsxHeaderRow
    };

    this.isSaving = true;
    this.collectorDatasetsService
      .createCollectorDataset$({
        file: selectedFile,
        schema_id: this.schemaId,
        schema_name: this.schemaName || this.schemaId,
        type: this.operationType,
        created_by_uid: this.currentUserData.uid,
        created_by: this.currentUserData.email || this.currentUserData.name || this.currentUserData.uid,
        ...(this.isXlsx && this.xlsxSheetName ? { sheet_name: this.xlsxSheetName } : {}),
        ...(this.isXlsx && this.xlsxHeaderRow != null ? { header_row: this.xlsxHeaderRow } : {}),
        ...(this.operationType === 'full_replace' ? { confirm: true } : {}),
        ...(scope ? { scope } : {})
      })
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (response: CreateCollectorDatasetResponse) => {
          this.selectedFile = null;
          this.fileName = '';
          this.isTrackingSubmission = true;
          this.liveDataset$ = this.collectorDatasetsService.watchDataset$(response.collector_dataset_id);
        },
        error: (error) => {
          const message = error?.error?.message || 'No se pudo guardar el dataset.';
          this.snackBar.open(message, 'Cerrar', {
            duration: 4000
          });
        }
      });
  }

  openLiveDetail(dataset: DatasetFile): void {
    this.dialog.open(CollectorDatasetDetailDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: dataset
    });
  }

  isLiveFailed(status?: ValidationStatus): boolean {
    return status === 'failed' || !!status?.startsWith('failed_');
  }

  isLiveDone(status?: ValidationStatus): boolean {
    return status === 'validated_metadata' || status === 'completed';
  }

  liveTrackerClass(status?: ValidationStatus): string {
    if (this.isLiveDone(status))          return 'tracker-success';
    if (this.isLiveFailed(status))       return 'tracker-error';
    if (status === 'processing' || status === 'validating' || status === 'queued' || status === 'pending' || status === 'uploading') return 'tracker-processing';
    return 'tracker-pending';
  }

  liveStatusLabel(status?: ValidationStatus): string {
    if (this.isLiveDone(status))          return 'Completado';
    if (status === 'failed_fields')      return 'Error de configuración';
    if (status === 'failed_storage')     return 'Error de archivo';
    if (status === 'failed_validation')  return 'Error de validación';
    if (status === 'failed_loading')     return 'Error al cargar';
    if (status === 'failed')             return 'Error';
    if (status === 'validating')         return 'Validando...';
    if (status === 'processing')         return 'Procesando...';
    if (status === 'queued')             return 'En cola...';
    if (status === 'uploading')          return 'Subiendo...';
    return 'Recibido';
  }

  liveStatusIcon(status?: ValidationStatus): string {
    if (this.isLiveDone(status))          return 'check_circle';
    if (this.isLiveFailed(status))       return 'error';
    if (status === 'processing' || status === 'validating') return 'sync';
    if (status === 'queued' || status === 'uploading')      return 'schedule';
    return 'inbox';
  }

  isLiveProcessing(status?: ValidationStatus): boolean {
    return status === 'processing' || status === 'validating' || status === 'queued' || status === 'pending' || status === 'uploading';
  }

  hasReachedProcessing(status?: ValidationStatus): boolean {
    return status === 'processing'
      || status === 'validating'
      || this.isLiveDone(status)
      || this.isLiveFailed(status);
  }

  hasReachedResult(status?: ValidationStatus): boolean {
    return this.isLiveDone(status) || this.isLiveFailed(status);
  }

  liveStatusDescription(dataset: DatasetFile): string {
    const status = dataset.validation_status;
    if (this.isLiveDone(status)) {
      return 'La importación terminó correctamente y los datos ya están disponibles.';
    }
    if (this.isLiveFailed(status)) {
      return 'La importación terminó con un problema. Revisa el detalle antes de intentarlo nuevamente.';
    }
    if (status === 'validating') {
      return 'Estamos verificando la estructura y el contenido del archivo.';
    }
    if (status === 'processing') {
      return 'El archivo es válido y estamos aplicando los datos al destino.';
    }
    if (status === 'queued') {
      return 'El archivo fue recibido y está esperando su turno para procesarse.';
    }
    if (status === 'uploading') {
      return 'Estamos guardando el archivo de forma segura antes de procesarlo.';
    }
    return 'La carga fue recibida y comenzará a procesarse en breve.';
  }

  missingFieldLabel(fieldName: string): string {
    const column = this.schemaColumns.find((col) => col.name === fieldName);
    return column?.label || fieldName;
  }

  parseValidationError(dataset?: DatasetFile | null): ParsedValidationError | null {
    const raw = (dataset?.validation_message || dataset?.last_error?.message || '').trim();
    if (this.parsedErrorCache?.raw === raw) return this.parsedErrorCache.value;

    const value = parseCollectorDatasetValidationError(dataset, (f) => this.missingFieldLabel(f));
    this.parsedErrorCache = { raw, value };
    return value;
  }

  errorHeadline(dataset?: DatasetFile | null): string {
    const parsed = this.parseValidationError(dataset);
    if (parsed && (parsed.missing.length || parsed.reason === 'headers_mismatch')) {
      return 'Los encabezados del archivo no coinciden con la estructura esperada del schema.';
    }
    return this.liveFailedDescription(dataset);
  }

  visibleMissingFields(fields: string[]): string[] {
    if (this.showAllMissingFields) return fields;
    return fields.slice(0, this.missingFieldsPreviewCount);
  }

  liveFailedDescription(dataset?: DatasetFile | null): string {
    if (!dataset) return '';
    if (dataset.validation_message) return dataset.validation_message;
    if (dataset.last_error?.message) return dataset.last_error.message;
    const status = dataset.validation_status;
    if (status === 'failed_fields')     return 'Problema con la configuración del schema o el mensaje.';
    if (status === 'failed_storage')    return 'El archivo no se encontró o no pudo leerse.';
    if (status === 'failed_validation') return 'El archivo no pasó la validación de columnas.';
    if (status === 'failed_loading')    return 'Error al insertar los datos en la base de datos.';
    return 'Ocurrió un error al procesar el archivo.';
  }

  get isXlsx(): boolean {
    return this.selectedFileType === 'xlsx';
  }

  private clearValidation(): void {
    this.liveDataset$ = EMPTY;
    this.isTrackingSubmission = false;
    this.isXlsxOptionsExpanded = false;
    this.xlsxSheetName = '';
    this.xlsxHeaderRow = null;
    this.operationType = 'insert';
    this.fullReplaceConfirm = false;
    this.scopeColumn = '';
    this.scopeValueSingle = '';
    this.showAllMissingFields = false;
    this.parsedErrorCache = null;
  }

  private buildScope(): DatasetOperationScope | null {
    if (this.operationType !== 'replace_scope') return null;
    return { column: this.scopeColumn, operator: 'eq', value: this.scopeValueSingle.trim() };
  }

  private formatFileSize(bytes: number): string {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  }

  downloadTemplateCsv(): void {
    const columns = this.schemaColumns;
    if (!columns.length) {
      this.snackBar.open('Este schema no tiene columnas para generar una plantilla.', 'Cerrar', {
        duration: 3500
      });
      return;
    }

    const headers = columns.map((col) => String(col.label || col.name || '').trim()).filter(Boolean);
    if (!headers.length) {
      this.snackBar.open('No se encontraron labels válidos en las columnas del schema.', 'Cerrar', {
        duration: 3500
      });
      return;
    }

    const headerLine = headers.map((v) => this.escapeCsvValue(v)).join(',');
    const exampleValues = columns.map((col) => {
      const ex = String(col.example ?? col.default_value ?? '').trim();
      return this.escapeCsvValue(ex);
    });
    const exampleLine = exampleValues.join(',');
    const csvContent = `${headerLine}\n${exampleLine}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const baseName = (this.schemaName || this.schemaId || 'schema')
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, '_');
    const fileName = `template_${baseName}.csv`;

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  openSchemaStructure(): void {
    if (!this.schemaDetails) {
      this.snackBar.open('No hay estructura de schema disponible para visualizar.', 'Cerrar', {
        duration: 3500
      });
      return;
    }

    this.dialog.open(SchemaStructureDialogComponent, {
      width: '1100px',
      maxWidth: '96vw',
      maxHeight: '88vh',
      autoFocus: false,
      data: {
        schemaName: this.schemaName || this.schemaId,
        schema: this.schemaDetails
      }
    });
  }

  get schemaPickerOptions() {
    return this.availableSchemas.map((schema) => ({
      label: schema.name || schema.id,
      value: schema.id
    }));
  }

  private loadAvailableSchemas(): void {
    this.isSchemasListLoading = true;
    this.schemasListError = '';
    this.schemasService.getAllSchemas$().subscribe({
      next: (schemas) => {
        this.isSchemasListLoading = false;
        this.availableSchemas = (schemas ?? []).slice().sort((a, b) =>
          (a.name || a.id).localeCompare(b.name || b.id)
        );
      },
      error: () => {
        this.isSchemasListLoading = false;
        this.schemasListError = 'No se pudo cargar la lista de schemas disponibles.';
      }
    });
  }

  onSchemaPicked(schemaId: string): void {
    const selected = this.availableSchemas.find((schema) => schema.id === schemaId);
    if (!selected) return;

    this.schemaId = selected.id;
    this.schemaName = selected.name || selected.id;
    this.schemaDetails = selected;
    this.schemaLoadError = '';
    this.isSchemaLoading = false;
    // Reinicia la configuración de operación (scope/columnas dependen del schema),
    // pero conserva el archivo que el usuario ya hubiera seleccionado.
    this.clearValidation();
  }

  private loadSchemaDetails(): void {
    if (!this.schemaId) {
      this.schemaDetails = null;
      this.schemaLoadError = 'No se recibió schema_id para cargar la estructura.';
      return;
    }

    this.isSchemaLoading = true;
    this.schemaLoadError = '';

    this.schemasService.getSchemaById$(this.schemaId).subscribe({
      next: (schema) => {
        this.isSchemaLoading = false;
        if (!schema) {
          this.schemaDetails = null;
          this.schemaLoadError = 'No se encontró el schema seleccionado.';
          return;
        }

        this.schemaDetails = schema;
        if (!this.schemaName) {
          this.schemaName = schema.name ?? this.schemaId;
        }
      },
      error: () => {
        this.isSchemaLoading = false;
        this.schemaDetails = null;
        this.schemaLoadError = 'No se pudo cargar la estructura del schema.';
      }
    });
  }

  private escapeCsvValue(value: string): string {
    const safe = value.replace(/"/g, '""');
    return /[",\n]/.test(safe) ? `"${safe}"` : safe;
  }

  private getFileType(fileName: string): 'csv' | 'xlsx' | null {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'csv' || extension === 'xlsx') {
      return extension;
    }
    return null;
  }

  private updateUploadPaneWidthFromClientX(clientX: number): void {
    if (typeof window === 'undefined') return;

    const viewportWidth = window.innerWidth;
    if (!viewportWidth) return;

    const rawPercent = (clientX / viewportWidth) * 100;
    this.uploadPaneWidthPercent = this.clampUploadPaneWidth(rawPercent);
  }

  private clampUploadPaneWidth(value: number): number {
    return Math.min(this.maxUploadPanePercent, Math.max(this.minUploadPanePercent, value));
  }

  private restoreUploadPaneWidth(): void {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(this.resizeStorageKey);
    if (!stored) return;
    const parsed = Number(stored);
    if (!Number.isFinite(parsed)) return;
    this.uploadPaneWidthPercent = this.clampUploadPaneWidth(parsed);
  }

  private persistUploadPaneWidth(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this.resizeStorageKey, String(this.uploadPaneWidthPercent));
  }

  private detachResizeListeners(): void {
    this.removeResizeListeners?.();
    this.removeResizeListeners = null;
  }

}
